import { ReactElement } from 'react';

import { ListingFunctionType } from '@/common/analytics/gtm/types';
import { trackMorePostersBtn } from '@/common/analytics/MasonryFeeds/MasonryFeeds.analytics';
import { MasonryFeedGrid } from '@/common/components/MasonryFeed/MasonryFeedGrid';
import { Feed, MIN_NUM_OF_POSTERS_FOR_FEED } from '@/common/components/MasonryFeed/MasonryFeeds';
import { FeedContents } from '@/common/components/MasonryFeed/MasonryFeedsSelector';
import { ComponentMetadata } from '@/common/components/types';
import { RouteToAllPostersPage } from '@/common/routes/ExternalRoutes';
import { capitalize } from '@/common/utils/sharedUtils';
import { GalleryItem } from 'generatedBfw/static';

import { LinkAllPosters } from './MasonryFeeds.styled';

export const createFeedFactory =
  <Feeds extends Record<string, GalleryItem[]>>(feeds: Feeds | undefined) =>
  (
    feedName: keyof Feeds,
    masonryListingFunction: ListingFunctionType,
    overrides?: Partial<Feed>,
  ): Feed => {
    const feed = feeds?.[feedName] ? feeds[feedName] : [];

    return {
      name: feedName as string,
      shouldBeShown: feed.length >= MIN_NUM_OF_POSTERS_FOR_FEED,
      masonryListingFunction: masonryListingFunction as ListingFunctionType,
      items: feed,
      ...overrides,
    };
  };

export const createMetadataForFeedGrid = (
  feed: Feed,
  masonryMetadata: ComponentMetadata,
): ComponentMetadata => {
  return {
    ...masonryMetadata,
    itemListName: capitalize(feed.name),
    listingFunction: feed.masonryListingFunction,
  };
};

const defaultMasonryRedirectButton = (): ReactElement => (
  <LinkAllPosters
    href={RouteToAllPostersPage().route}
    onClick={() => trackMorePostersBtn('see_all_posters_button')}
    target="_blank"
  >
    See all posters
  </LinkAllPosters>
);

export const createFeedContentComponent = (
  feed: Feed,
  metadata: ComponentMetadata,
): FeedContents => {
  let content: ReactElement | undefined;

  if (feed.overrideGridComponent) {
    content = <feed.overrideGridComponent />;
  } else {
    content = (
      <MasonryFeedGrid
        feedName={feed.name}
        infiniteScroll={feed.infiniteScroll}
        items={feed.items}
        key={feed.name}
        metadata={createMetadataForFeedGrid(feed, metadata)}
        redirectButton={feed.redirectButton || defaultMasonryRedirectButton}
      />
    );
  }

  return {
    heading: feed.name,
    content,
  };
};

export const areAllFeedsHidden = (feeds: Feed[]) => {
  return feeds.every((feed) => feed.shouldBeShown === false);
};
